import { useEffect, useState, useCallback } from 'react'
import { Smartphone, Plus, RefreshCw, X, ChevronRight, CheckCircle2, AlertTriangle } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import SearchableSelect from '../components/SearchableSelect'
import { successModal, errorModal, confirmModal } from '../components/ui'

// Nova Conexão → TELECOM (CSMVNO): ativação de chip, compra (físico/e-SIM) e portabilidade.
// Pagamento v1 = PIX. Comissão pelo motor MLM quando a venda fica ATIVA.
const ST = {
  iniciada: { label: 'Iniciada', cls: 'bg-neutral-100 text-neutral-700 border-neutral-200' },
  aguardando_pagamento: { label: 'Aguardando Pagamento', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  paga: { label: 'Paga', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  ativa: { label: 'Ativa', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  concluida: { label: 'Concluída', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  cancelada: { label: 'Cancelada', cls: 'bg-neutral-200 text-neutral-600 border-neutral-300' },
  falha: { label: 'Falha', cls: 'bg-red-100 text-red-800 border-red-300' },
}
const TIPOS = [
  ['ativacao', 'Ativação', 'Ativar uma nova linha — escolha o plano e gere o 1º pagamento'],
  ['portabilidade', 'Portabilidade', 'Trazer o número do cliente de outra operadora — escolha o plano e gere o 1º pagamento'],
]
function QrEsim({ q }) {
  if (!q) return null
  if (typeof q === 'object' && q.url_pdf) return <a href={q.url_pdf} target="_blank" rel="noreferrer" className="text-[10px] text-accent-700 underline">PDF do e-SIM (QR)</a>
  if (typeof q === 'object' && q.pdf_base64) return <a href={`data:application/pdf;base64,${q.pdf_base64}`} download={q.arquivo || 'esim.pdf'} className="text-[10px] text-accent-700 underline">PDF do e-SIM (QR)</a>
  const val = typeof q === 'string' ? q : (q.qrcode || q.qr_code || q.imagem || q.base64 || q.url || q.lpa || q.codigo || '')
  if (!val) return null
  const s = String(val)
  if (/^https?:\/\//.test(s)) return <a href={s} target="_blank" rel="noreferrer" className="text-[10px] text-accent-700 underline">QR do e-SIM</a>
  if (s.startsWith('data:') || s.length > 200) {
    const src = s.startsWith('data:') ? s : `data:image/png;base64,${s}`
    return <a href={src} target="_blank" rel="noreferrer" className="text-[10px] text-accent-700 underline">QR do e-SIM</a>
  }
  return <p className="text-[10px] font-mono text-neutral-500 break-all select-all" title="Código de instalação do e-SIM">{s}</p>
}
const Chip = ({ s }) => <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${(ST[s] || {}).cls || ''}`}>{(ST[s] || {}).label || s}</span>
const money = (v) => (v == null ? '-' : 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }))
const fmt = (v) => (v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-')
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'

const CAMPOS = {
  ativacao: [['nome_completo', 'Nome completo *'], ['cpf_cnpj', 'CPF/CNPJ *'], ['ddd', 'DDD (só o código, ex.: 11) *'], ['celular', 'Celular p/ contato *'],
    ['email', 'E-mail *'], ['data_nascimento', 'Nascimento (DD/MM/AAAA)'], ['cep', 'CEP *'], ['logradouro', 'Endereço *'], ['cidade', 'Cidade *'], ['uf', 'UF *'],
    ['iccid', 'Chip / ICCID (buscar do estoque ou digitar) *']],
  portabilidade: [['nome_completo', 'Nome completo *'], ['cpf_cnpj', 'CPF/CNPJ *'], ['email', 'E-mail *'],
    ['operadora', 'Operadora de origem *'], ['numero_portado', 'Número a portar (DDD+número) *'],
    ['cep', 'CEP *'], ['logradouro', 'Endereço *'], ['cidade', 'Cidade *'], ['uf', 'UF *'],
    ['iccid', 'Chip / ICCID (buscar do estoque ou digitar) *']],
}

// Paleta de identidade dos planos (mesma vibe do flyer: verde→amarelo→azul→roxo→laranja→vermelho...).
const PLANO_CORES = ['#16a34a', '#eab308', '#2563eb', '#7c3aed', '#ea580c', '#dc2626', '#0d9488', '#db2777']

function NovaVenda({ isAdmin, onClose, onDone }) {
  const [passo, setPasso] = useState(1)
  const [tipo, setTipo] = useState('')
  const [planos, setPlanos] = useState(null)   // null=carregando
  const [planoId, setPlanoId] = useState('')
  const [f, setF] = useState({})
  const [lics, setLics] = useState([])
  const [licId, setLicId] = useState('')
  const [busy, setBusy] = useState(false)
  const [criada, setCriada] = useState(null)
  const [cepMsg, setCepMsg] = useState('')
  const [chipMsg, setChipMsg] = useState('')

  // Busca automática de um chip (ICCID) disponível no estoque — preenche o campo (editável manualmente).
  async function buscarChip() {
    setChipMsg('buscando...')
    try {
      const tipoChip = f.tipo_chip || 'fisico'
      const { data } = await api.get('/telecom/iccid-disponivel', { params: { tipo: tipoChip } })
      if (data?.iccid) { setF((s) => ({ ...s, iccid: data.iccid })); setChipMsg(tipoChip === 'esim' ? 'e-SIM do estoque' : 'chip do estoque') }
      else setChipMsg('sem chip no estoque')
    } catch (e) { setChipMsg(e?.response?.data?.detail || 'sem chip disponível') }
  }

  // Busca automática de endereço pelo CEP (ViaCEP) — preenche logradouro/cidade/uf (ajustável pelo usuário).
  async function buscarCep(raw) {
    const cep = String(raw || '').replace(/\D/g, '')
    if (cep.length !== 8) return
    setCepMsg('buscando...')
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const d = await r.json()
      if (d.erro) { setCepMsg('CEP não encontrado'); return }
      setF((s) => ({ ...s, logradouro: d.logradouro || s.logradouro || '', cidade: d.localidade || s.cidade || '', uf: d.uf || s.uf || '' }))
      setCepMsg('endereço preenchido')
    } catch { setCepMsg('') }
  }

  useEffect(() => {
    api.get('/telecom/planos').then((r) => setPlanos(r.data.planos || [])).catch((e) => { setPlanos([]); })
    if (isAdmin) api.get('/licenciados?limit=200').then((r) => setLics(r.data.data || [])).catch(() => {})
  }, [isAdmin])

  async function enviar() {
    for (const [k, l] of CAMPOS[tipo] || []) {
      if (l.includes('*') && !String(f[k] || '').trim()) return errorModal(`Preencha: ${l.replace(' *', '')}`)
    }
    if (!planoId) return errorModal('Selecione o plano.')
    if (isAdmin && !licId) return errorModal('Selecione o licenciado responsável.')
    if (!(await confirmModal('Registrar a solicitação e gerar a cobrança PIX da 1ª mensalidade (The Conect)?', { title: 'Registrar solicitação', okLabel: 'Confirmar' }))) return
    setBusy(true)
    try {
      const { data } = await api.post('/telecom/vendas', {
        tipo, id_plano: planoId || undefined,
        dados: { ...f, tipo_chip: f.tipo_chip || 'fisico' },
        licenciado_id: isAdmin ? Number(licId) : undefined,
      })
      setCriada(data)
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao enviar') } finally { setBusy(false) }
  }

  if (criada) {
    const rp = criada.resposta_parceiro || {}
    const pix = rp.pix || {}
    const copiaCola = pix.copia_cola || ''
    const qr = pix.imagem ? (String(pix.imagem).startsWith('data:') ? pix.imagem : `data:image/png;base64,${pix.imagem}`) : null
    const copiar = () => { try { navigator.clipboard.writeText(copiaCola); successModal('Código PIX copiado!') } catch { /* */ } }
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 text-center space-y-3">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 size={28} className="text-emerald-600" /></div>
        <p className="font-heading font-bold text-graphite text-lg">Solicitação {criada.codigo} registrada!</p>
        <p className="text-sm text-neutral-500">Status: <Chip s={criada.status} /></p>
        {copiaCola ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-graphite">Pague a 1ª mensalidade via PIX para concluir</p>
            {qr && <img src={qr} alt="QR Code PIX" className="w-40 h-40 mx-auto rounded-lg border border-neutral-200" />}
            <p className="text-[11px] text-neutral-500">PIX copia e cola:</p>
            <p className="text-[11px] bg-neutral-50 rounded-lg p-3 break-all select-all">{copiaCola}</p>
            <button onClick={copiar} className="text-xs font-bold text-accent-700 border border-accent-200 rounded-lg px-3 py-1.5 hover:bg-accent-50">Copiar código PIX</button>
            {rp.invoice_url && <a href={rp.invoice_url} target="_blank" rel="noreferrer" className="block text-xs text-accent-700 underline">Abrir fatura / outras formas de pagamento</a>}
          </div>
        ) : rp.boleto?.url ? (
          <a href={rp.boleto.url} target="_blank" rel="noreferrer" className="block text-sm text-accent-700 underline">Abrir boleto da 1ª mensalidade</a>
        ) : rp.invoice_url ? (
          <a href={rp.invoice_url} target="_blank" rel="noreferrer" className="block text-sm text-accent-700 underline">Abrir cobrança da 1ª mensalidade</a>
        ) : (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">Solicitação registrada, mas a cobrança PIX não pôde ser gerada agora{criada.ultima_msg ? ` — ${criada.ultima_msg}` : ''}. Use "Gerar cobrança novamente" na lista para tentar de novo.</p>
        )}
        <p className="text-xs text-neutral-400">A 1ª mensalidade é paga à <b>The Conect</b>. Após a confirmação do pagamento, a linha é ativada/portada na operadora e as mensalidades seguintes passam a ser cobradas por ela. Use "Verificar pagamento" na lista para confirmar.</p>
        <button onClick={onDone} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 text-sm font-bold shadow-md">Ver minhas solicitações</button>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-heading font-bold text-graphite">Nova venda Telecom</p>
        <button onClick={onClose} className="text-neutral-400 hover:text-graphite"><X size={18} /></button>
      </div>

      {passo === 1 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {TIPOS.map(([v, l, desc]) => (
            <button key={v} onClick={() => { setTipo(v); setPasso(2) }}
              className="text-left rounded-2xl border border-gray-200 p-4 hover:border-accent-400 hover:shadow-md transition">
              <p className="font-semibold text-gray-900 text-sm flex items-center gap-1">{l} <ChevronRight size={14} className="text-accent-500" /></p>
              <p className="text-[11px] text-gray-500 mt-1">{desc}</p>
            </button>
          ))}
        </div>
      )}

      {passo === 2 && (
        <div>
          <p className="text-sm font-semibold text-gray-800 mb-2">Escolha o plano</p>
          {planos === null ? <Loader text="Consultando planos na operadora..." /> : planos.length === 0 ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-center gap-1.5">
              <AlertTriangle size={15} /> A operadora ainda não liberou planos para o contrato The Conect. Solicite a liberação à CSMVNO.
            </p>
          ) : (
            <div className="grid sm:grid-cols-3 gap-2">
              {planos.map((p, i) => {
                const pid = String(p.id ?? p.id_plano ?? i)
                const cor = PLANO_CORES[i % PLANO_CORES.length]
                const preco = p.valor || p.preco || p.vlr_venda
                const sel = planoId === pid
                return (
                  <button key={pid} onClick={() => { setPlanoId(pid); setPasso(3) }}
                    style={{ borderLeftColor: cor, background: sel ? cor + '14' : undefined, boxShadow: sel ? `0 0 0 2px ${cor}` : undefined }}
                    className="text-left rounded-xl border border-gray-200 border-l-4 p-3 transition hover:shadow-md">
                    <p className="text-sm font-semibold text-gray-900">{p.nome || p.descricao || `Plano ${pid}`}</p>
                    {preco && <p className="font-heading font-extrabold" style={{ color: cor }}>{money(preco)}</p>}
                    {p.franquia && <p className="text-[11px] text-gray-500">{p.franquia}</p>}
                  </button>
                )
              })}
            </div>
          )}
          <button onClick={() => setPasso(1)} className="mt-3 text-xs text-gray-500 hover:underline">← Voltar</button>
        </div>
      )}

      {passo === 3 && (
        <div className="space-y-3">
          <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 flex items-center gap-1.5">
            <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" /> Use "Buscar estoque" para puxar um chip automaticamente, ou digite o ICCID manualmente · endereço preenchido pelo CEP.
          </p>
          {isAdmin && (
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Licenciado responsável *</label>
              <SearchableSelect value={licId} onChange={setLicId} placeholder="Buscar licenciado..." options={lics.map((l) => ({ value: l.id, label: l.nome }))} /></div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de pessoa *</label>
            <div className="flex gap-2">
              {[['F', 'Pessoa Física (CPF)'], ['J', 'Pessoa Jurídica (CNPJ)']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setF((s) => ({ ...s, tipo_pessoa: v }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${(f.tipo_pessoa || 'F') === v ? 'bg-accent-500 text-white border-accent-500' : 'border-gray-200 text-gray-600 hover:border-accent-300'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de chip *</label>
            <div className="flex gap-2">
              {[['fisico', 'Chip físico'], ['esim', 'e-SIM (digital)']].map(([v, l]) => (
                <button key={v} type="button"
                  onClick={() => { setF((s) => ({ ...s, tipo_chip: v, iccid: (s.tipo_chip || 'fisico') === v ? s.iccid : '' })); setChipMsg('') }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${(f.tipo_chip || 'fisico') === v ? 'bg-accent-500 text-white border-accent-500' : 'border-gray-200 text-gray-600 hover:border-accent-300'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(CAMPOS[tipo] || []).map(([k, l0]) => {
              const l = k === 'cpf_cnpj' ? ((f.tipo_pessoa || 'F') === 'J' ? 'CNPJ *' : 'CPF *') : l0
              return (
              <div key={k}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{l}
                  {k === 'cep' && cepMsg ? <span className="text-accent-600 ml-1">· {cepMsg}</span> : ''}
                  {k === 'iccid' && chipMsg ? <span className="text-accent-600 ml-1">· {chipMsg}</span> : ''}
                </label>
                <div className={k === 'iccid' ? 'flex gap-2' : ''}>
                  <input className={inp} value={f[k] || ''}
                    onChange={(e) => { const v = e.target.value; setF((s) => ({ ...s, [k]: v })); if (k === 'cep' && v.replace(/\D/g, '').length === 8) buscarCep(v) }}
                    onBlur={(e) => { if (k === 'cep') buscarCep(e.target.value) }} />
                  {k === 'iccid' && (
                    <button type="button" onClick={buscarChip}
                      className="whitespace-nowrap text-xs font-bold text-accent-700 border border-accent-200 rounded-lg px-3 hover:bg-accent-50">Buscar estoque</button>
                  )}
                </div>
              </div>
            )})}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPasso(2)} className="text-xs text-gray-500 hover:underline px-2">← Voltar</button>
            <button onClick={enviar} disabled={busy}
              className="flex-1 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2.5 text-sm font-bold shadow-md disabled:opacity-50">
              {busy ? 'Gerando cobrança...' : 'Registrar solicitação e gerar PIX'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Telecom() {
  const isAdmin = (JSON.parse(localStorage.getItem('user') || '{}').perfil || '') === 'admin'
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [nova, setNova] = useState(false)
  const [busy, setBusy] = useState(0)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/telecom/vendas?limit=50').then((r) => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  async function verificar(v) {
    setBusy(v.id)
    try {
      const { data } = await api.post(`/telecom/vendas/${v.id}/consultar-pagamento`)
      const cred = data.resultado ? ` PV ${data.resultado.pv} e comissões distribuídas (${money(data.resultado.comissao)}).` : ''
      if (data.pago && data.provisionado) await successModal(`Pagamento confirmado e LINHA ATIVADA na operadora!${cred}`, { title: 'Linha ativa!' })
      else if (data.pago) await successModal(`Pagamento confirmado.${cred} Ativação na operadora PENDENTE: ${data.provisionamento_msg || 'sem retorno'}. Use "Reenviar ativação" para tentar novamente.`, { title: 'Pago — ativação pendente' })
      else await successModal('Pagamento ainda não identificado no gateway.', { title: 'Aguardando' })
      load()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro na consulta') } finally { setBusy(0) }
  }

  // Regera a cobrança PIX quando a 1ª geração falhou (ex.: ASAAS instável) — sem recriar a venda.
  async function gerarCobranca(v) {
    setBusy(v.id)
    try {
      const { data } = await api.post(`/telecom/vendas/${v.id}/gerar-cobranca`)
      await successModal(`Cobrança PIX gerada — ${data.codigo} agora está aguardando pagamento.`, { title: 'Cobrança gerada' })
      load()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao gerar cobrança') } finally { setBusy(0) }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2">
            <Smartphone size={24} className="text-accent-500" /> Telecom — Minhas Vendas
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Ativação e Portabilidade de planos móveis (Surf Telecom) — 1º pagamento pela plataforma, recorrência pela operadora</p>
        </div>
        <button onClick={() => setNova(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-bold shadow-md"><Plus size={16} /> Nova venda</button>
      </div>

      {nova && <NovaVenda isAdmin={isAdmin} onClose={() => setNova(false)} onDone={() => { setNova(false); load() }} />}

      {loading ? <div className="flex justify-center py-16"><Loader text="Carregando..." /></div> : (
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-gradient-to-r from-primary-50 to-accent-50 border-b border-primary-100">
                {['Cliente', 'Tipo', 'Plano', 'ICCID', 'Valor', 'Data', 'Status', ''].map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider ${i >= 3 ? 'text-center' : 'text-left'}`}>{h}</th>))}
              </tr></thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-14 text-neutral-400"><Smartphone size={38} className="mx-auto mb-2 opacity-30" /><p>Nenhuma venda Telecom ainda</p></td></tr>
                ) : rows.map((v) => (
                  <tr key={v.id} className="border-b border-neutral-100 hover:bg-primary-50/40">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-graphite">{v.cliente_nome || '-'}</p>
                      <p className="text-[11px] text-neutral-400">{v.codigo}{v.telefone ? ` · ${v.telefone}` : ''}{isAdmin && v.licenciado_nome ? ` · ${v.licenciado_nome}` : ''}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600">{({ ativacao: 'Ativação', compra_chip: 'Compra de chip', portabilidade: 'Portabilidade' })[v.tipo] || v.tipo}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600">{v.plano_nome || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <p className="text-xs font-mono text-neutral-600" title="ICCID do chip">{v.iccid || '—'}</p>
                      <p className="text-[10px] text-neutral-400">{v.tipo_chip === 'esim' ? 'e-SIM' : 'Chip físico'}{v.numero_linha ? ` · linha ${v.numero_linha}` : ''}</p>
                      <QrEsim q={v.qrcode_esim} />
                    </td>
                    <td className="px-4 py-3 text-center text-sm">{money(v.valor)}</td>
                    <td className="px-4 py-3 text-center text-xs text-neutral-500">{fmt(v.created_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <Chip s={v.status} />
                      {(v.status === 'paga' || v.status === 'falha') && v.ultima_msg && (
                        <p className="text-[10px] text-neutral-500 mt-1 max-w-[260px] mx-auto leading-tight" title={v.ultima_msg}>{v.ultima_msg}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {v.status === 'aguardando_pagamento' && v.pagamento_ref && (
                        <button onClick={() => verificar(v)} disabled={busy === v.id} title="Verificar pagamento"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-accent-700 border border-accent-200 rounded-lg px-2.5 py-1.5 hover:bg-accent-50 disabled:opacity-50">
                          <RefreshCw size={12} className={busy === v.id ? 'animate-spin' : ''} /> Verificar pagamento
                        </button>
                      )}
                      {v.status === 'paga' && (
                        <button onClick={() => verificar(v)} disabled={busy === v.id} title="Pago — reenviar a ativação para a operadora"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 border border-amber-300 rounded-lg px-2.5 py-1.5 hover:bg-amber-50 disabled:opacity-50">
                          <RefreshCw size={12} className={busy === v.id ? 'animate-spin' : ''} /> Reenviar ativação
                        </button>
                      )}
                      {v.status === 'falha' && (
                        <button onClick={() => gerarCobranca(v)} disabled={busy === v.id} title="Gerar a cobrança PIX novamente"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 border border-red-200 rounded-lg px-2.5 py-1.5 hover:bg-red-50 disabled:opacity-50">
                          <RefreshCw size={12} className={busy === v.id ? 'animate-spin' : ''} /> Gerar cobrança novamente
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
